import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, toast } from '@wit-support/ui';
import { PipelineDisabledDot, RailCard } from '@wit-support/ui-domain';
import { Play, Workflow } from 'lucide-react';
import { listEligiblePipelines, PipelineAlreadyRunningError, PipelineNotEligibleError, runPipeline, } from '../api.js';
/**
 * Wording for the 409 the manual-run button can hit. A `waiting` blocker names
 * WHO is being waited on (and until when) — an operator reading «уже
 * выполняется» would sit tight for a run that is parked, not busy.
 */
export function waitingForCustomerToast(err) {
    if (err.reason !== 'waiting_for_customer')
        return 'Пайплайн уже выполняется';
    const d = err.waitDeadline ? new Date(err.waitDeadline) : null;
    const until = d && !Number.isNaN(d.getTime())
        ? ` до ${d.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        })}`
        : '';
    return `Пайплайн ждёт ответа клиента${until}`;
}
/**
 * Lists the pipelines whose trigger matches this conversation's classification
 * (incl. disabled ones) and lets a non-viewer manually run any of them. A run
 * is fire-and-forget (202 ack); the worker executes it and the card re-renders
 * from DB state. Renders nothing while loading / on error to avoid rail clutter;
 * shows a muted note when nothing matches.
 */
export function PipelinesPanel({ conversationId, canRun, }) {
    const queryClient = useQueryClient();
    const [runningKey, setRunningKey] = useState(null);
    const query = useQuery({
        queryKey: ['eligible-pipelines', conversationId],
        queryFn: () => listEligiblePipelines(conversationId),
    });
    const runMut = useMutation({
        mutationFn: (key) => runPipeline(conversationId, key),
        onMutate: (key) => setRunningKey(key),
        onSuccess: () => {
            toast.success('Пайплайн запущен');
            void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
            void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
            void queryClient.invalidateQueries({ queryKey: ['pipeline-runs', conversationId] });
        },
        onError: (err) => {
            if (err instanceof PipelineAlreadyRunningError) {
                // Still eligible — a run is just in-flight. Don't refetch the eligible
                // list; refresh run-history so the in-flight run shows up.
                //
                // «Уже выполняется» is a lie for a PARKED run: nothing is executing, the
                // bot is waiting on the customer and will keep waiting until the
                // deadline. An operator told the wrong thing waits for a run that is not
                // coming instead of just answering by hand (which un-parks it).
                toast.error(waitingForCustomerToast(err));
                void queryClient.invalidateQueries({ queryKey: ['pipeline-runs', conversationId] });
                return;
            }
            if (err instanceof PipelineNotEligibleError) {
                toast.error('Пайплайн больше не подходит');
                void queryClient.invalidateQueries({ queryKey: ['eligible-pipelines', conversationId] });
                return;
            }
            toast.error(err.message);
        },
        onSettled: () => setRunningKey(null),
    });
    // Loading / error → render nothing (no rail clutter), consistent with the
    // SubscriptionPanel's loading behaviour.
    if (query.isLoading || query.isError || !query.data)
        return null;
    const pipelines = query.data.pipelines;
    return (_jsx(RailCard, { icon: Workflow, title: "\u041F\u0430\u0439\u043F\u043B\u0430\u0439\u043D\u044B", children: pipelines.length === 0 ? (_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "\u041D\u0435\u0442 \u043F\u043E\u0434\u0445\u043E\u0434\u044F\u0449\u0438\u0445 \u043F\u0430\u0439\u043F\u043B\u0430\u0439\u043D\u043E\u0432" })) : (pipelines.map((p) => (_jsxs("div", { className: "flex items-center justify-between gap-2 rounded-md border border-[var(--color-border-default)] bg-[var(--color-surface-default)] px-2.5 py-2", children: [_jsxs("div", { className: "flex min-w-0 items-center gap-1.5", children: [!p.enabled ? _jsx(PipelineDisabledDot, {}) : null, _jsx("span", { className: `truncate ${p.enabled ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-muted)]'}`, title: p.name, children: p.name })] }), canRun ? (_jsxs(Button, { variant: "ghost", size: "sm", disabled: runMut.isPending, onClick: () => runMut.mutate(p.key), "aria-label": `Запустить пайплайн ${p.name}`, children: [_jsx(Play, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), runningKey === p.key && runMut.isPending ? 'Запуск…' : 'Запустить'] })) : null] }, p.key)))) }));
}
//# sourceMappingURL=pipelines-panel.js.map